import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Bell, Send, Users, History, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationAdmin = () => {
  const { token, isAdmin } = useAuth();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ count: 0 });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats({ count: res.data.count || 0 });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
      setShowHistory(true);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Inserisci titolo e messaggio');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/notifications/send`, {
        title,
        body,
        target: 'all'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(res.data.message);
      setTitle('');
      setBody('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="bg-slate-900/60 border border-amber-500/30 rounded-xl p-4 mb-6">
      <h3 className="text-amber-400 font-bold flex items-center gap-2 mb-4">
        <Bell className="w-5 h-5" />
        Notifiche Push
      </h3>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-slate-800/50 rounded-lg">
        <Users className="w-5 h-5 text-cyan-400" />
        <div>
          <p className="text-white font-bold">{stats.count}</p>
          <p className="text-xs text-slate-400">Dispositivi registrati</p>
        </div>
        <button
          onClick={fetchHistory}
          className="ml-auto px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg flex items-center gap-1"
        >
          <History className="w-3 h-3" />
          Storico
        </button>
      </div>

      {/* Form */}
      <div className="space-y-3">
        <div>
          <label className="text-xs text-slate-400 uppercase">Titolo</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            placeholder="Es: Nuovo evento in arrivo!"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 uppercase">Messaggio</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            rows={2}
            placeholder="Es: Partecipa alla raccolta di sabato..."
          />
        </div>
        <button
          onClick={handleSend}
          disabled={loading || stats.count === 0}
          className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold rounded-lg flex items-center justify-center gap-2"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Send className="w-5 h-5" />
              Invia a tutti ({stats.count})
            </>
          )}
        </button>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowHistory(false)}>
          <div className="bg-slate-900 border border-amber-500/50 rounded-xl p-4 max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h4 className="text-amber-400 font-bold mb-4">Storico Notifiche</h4>
            {history.length === 0 ? (
              <p className="text-slate-400 text-center py-4">Nessuna notifica inviata</p>
            ) : (
              <div className="space-y-3">
                {history.map((n, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-white font-medium">{n.title}</p>
                    <p className="text-slate-400 text-sm">{n.body}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <span className="text-emerald-400">{n.sent_count} inviati</span>
                      {n.failed_count > 0 && <span className="text-red-400">{n.failed_count} falliti</span>}
                      <span className="ml-auto">{new Date(n.sent_at).toLocaleDateString('it-IT')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowHistory(false)}
              className="w-full mt-4 py-2 bg-slate-700 text-slate-300 rounded-lg"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationAdmin;
